import BorderColorIcon from "@mui/icons-material/BorderColor";
import CheckIcon from "@mui/icons-material/Check";
import InfoIcon from "@mui/icons-material/Info";
import { Alert, Box, useTheme } from "@mui/material";
import { type DocumentPreviewProps } from "components/Document";
import { FilePreview } from "components/Document/shared";
import { SignatureType, SubmissionStatus } from "generated-graphql/graphql";
import { SubmissionReviewState } from "hooks/submission/reviewStates";
import { useOuterScrollUntilInView } from "hooks/useOuterScrollUntilInView";
import { useMemo } from "react";

type SubmissionDocumentPreviewProps = {
  previewDocumentUrl: string;
  fileExtension?: string;
  reviewState: SubmissionReviewState;
  signingCompleted: boolean;
  signingPending?: boolean;
  signatureType?: SignatureType | null;
  submissionStatus?: SubmissionStatus | null;
  isDocumentPreparedForSigning: boolean;
  isSigner: boolean;
  onSigningDialogOpen: () => void;
};

export function SubmissionDocumentPreview({
  previewDocumentUrl,
  fileExtension,
  reviewState,
  signingCompleted,
  signingPending = false,
  signatureType,
  submissionStatus,
  isDocumentPreparedForSigning,
  isSigner,
  onSigningDialogOpen,
}: SubmissionDocumentPreviewProps) {
  const theme = useTheme();

  const isWetInkSigner = isSigner && signatureType === SignatureType.WetInk;

  const signDocumentProps: DocumentPreviewProps["bottomLeftButtonProps"] | undefined =
    useMemo(() => {
      // Don't show sign button for wet ink signatures
      if (isWetInkSigner) {
        return undefined;
      }

      // A review turn alone is not enough to sign. The selected submission
      // document must still have an active BoldSign request, and the submission
      // must have actually entered customer review.
      if (
        signatureType !== SignatureType.Digital ||
        submissionStatus !== SubmissionStatus.InReview ||
        !isDocumentPreparedForSigning
      ) {
        return undefined;
      }

      if (
        reviewState === SubmissionReviewState.SIGNER_CAN_SIGN ||
        reviewState === SubmissionReviewState.SIGNER_IS_WAITING_FOR_REVIEWERS
      ) {
        let text = "Sign Document";
        if (signingCompleted) {
          text = "Signature Completed";
        } else if (signingPending) {
          text = "Confirming Signature";
        }

        return {
          // Show different text and icon depending on the signing status
          variant: "outlined",
          text,
          startIcon: signingCompleted ? <CheckIcon /> : <BorderColorIcon />,

          // If the signer cannot sign yet then show the button as disabled
          disabled:
            reviewState !== SubmissionReviewState.SIGNER_CAN_SIGN ||
            signingCompleted ||
            signingPending,
          sx: {
            paddingY: theme.spacing(3),
            paddingX: theme.spacing(4),
            background: signingCompleted ? "#E6F1F7" : "#EAF0EA",
          },
          color: signingCompleted ? "info" : "success",
          onClick: onSigningDialogOpen,
        };
      }
      return undefined;
    }, [
      reviewState,
      isWetInkSigner,
      onSigningDialogOpen,
      theme,
      signingCompleted,
      signingPending,
      signatureType,
      submissionStatus,
      isDocumentPreparedForSigning,
    ]);

  const wrapperRef = useOuterScrollUntilInView();

  return (
    <Box
      ref={wrapperRef}
      sx={{
        // Shorter than the stepper's scroll column so a wheel always has
        // somewhere to land that scrolls the page rather than the PDF. Falls
        // back to the viewport outside the stepper.
        minHeight: 400,
        height: "calc(var(--stepper-scroll-height, 100vh) * 0.8)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {isWetInkSigner && (
        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
          This document requires a wet ink signature. Your Encamp contact will reach out to
          coordinate physical document signing.
        </Alert>
      )}
      <Box sx={{ flex: 1, position: "relative", overflow: "hidden", minHeight: 0 }}>
        <Box sx={{ position: "absolute", inset: 0 }}>
          <FilePreview
            fileUrl={previewDocumentUrl}
            fileExtension={fileExtension}
            bottomLeftButtonProps={signDocumentProps}
          />
        </Box>
      </Box>
    </Box>
  );
}
